/**
 * Tag API service for admin interface
 * Handles CRUD operations for tags
 */

import type { Tag } from '@musedrops/shared';
import {
  getAllTags,
  getTagById,
  getTagByName,
  createTag as createTagRecord,
  updateTag as updateTagRecord,
  deleteTag as deleteTagRecord,
  getTagsUsedInPrompts,
  getTagsUsedInPromptsWithKeys,
  getTagUsageCounts,
  normalizeTagName,
  isValidTagName,
} from '../data/tag';

export class TagApi {
  static async getAll(): Promise<Tag[]> {
    return getAllTags();
  }

  static async getById(id: string): Promise<Tag> {
    return getTagById(id);
  }

  static async getByName(tag: string): Promise<Tag | null> {
    return getTagByName(tag);
  }

  static async create(tag: string): Promise<Tag> {
    return createTagRecord(tag);
  }

  static async update(id: string, newTag: string): Promise<Tag> {
    // Check if tag is used in prompts
    const tagsInPrompts = await getTagsUsedInPrompts();
    const currentTag = await getTagById(id);

    if (tagsInPrompts.has(currentTag.tag)) {
      throw new Error('Cannot rename tag: It is used in prompt templates');
    }

    return updateTagRecord(id, newTag);
  }

  static async delete(id: string): Promise<void> {
    // Check if tag is used in prompts
    const tagsInPrompts = await getTagsUsedInPrompts();
    const tag = await getTagById(id);

    if (tagsInPrompts.has(tag.tag)) {
      throw new Error('Cannot delete tag: It is used in prompt templates');
    }

    await deleteTagRecord(id);
  }

  static async getUsageCounts(
    tagName: string
  ): Promise<{ shows: number; stories: number }> {
    return getTagUsageCounts(tagName);
  }

  static async getTagsUsedInPrompts(): Promise<Set<string>> {
    return getTagsUsedInPrompts();
  }

  static async getTagsUsedInPromptsWithKeys(): Promise<Map<string, string[]>> {
    return getTagsUsedInPromptsWithKeys();
  }

  static normalizeTagName(tag: string): string {
    return normalizeTagName(tag);
  }

  static isValidTagName(tag: string): boolean {
    return isValidTagName(tag);
  }
}
